import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="login-overlay">
      <!-- Change Password Modal -->
      <div class="login-card" *ngIf="showChangePassword">
        <h2 class="login-title">Changement de mot de passe requis</h2>
        <p class="login-subtitle">Veuillez définir un nouveau mot de passe pour {{ email }}</p>

        <form (ngSubmit)="handleChangePassword()" class="login-form">
          <div class="form-group">
            <label for="new-password">Nouveau mot de passe</label>
            <input
              id="new-password"
              type="password"
              [(ngModel)]="newPassword"
              name="newPassword"
              required
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="confirm-password">Confirmer le mot de passe</label>
            <input
              id="confirm-password"
              type="password"
              [(ngModel)]="confirmPassword"
              name="confirmPassword"
              required
              class="form-input"
            />
          </div>

          <div class="error-message" *ngIf="error">{{ error }}</div>

          <button type="submit" [disabled]="isLoading" class="login-btn">
            {{ isLoading ? 'Changement...' : 'Changer le mot de passe' }}
          </button>
        </form>
      </div>

      <!-- Login Modal -->
      <div class="login-card" *ngIf="!showChangePassword">
        <div class="login-header">
          <span class="brand-icon">📊</span>
          <h2 class="login-title">KPI COA</h2>
        </div>
        <p class="login-subtitle">Connectez-vous à votre compte</p>

        <form (ngSubmit)="handleLogin()" class="login-form">
          <div class="form-group">
            <label for="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              [(ngModel)]="email"
              name="email"
              required
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="login-password">Mot de passe</label>
            <input
              id="login-password"
              type="password"
              [(ngModel)]="password"
              name="password"
              required
              class="form-input"
            />
          </div>

          <div class="error-message" *ngIf="error">{{ error }}</div>

          <button type="submit" [disabled]="isLoading" class="login-btn">
            {{ isLoading ? 'Connexion...' : 'Se connecter' }}
          </button>
        </form>
      </div>
    </div>
  `,
  styles: [`
    .login-overlay {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      z-index: 1000;
    }

    .login-card {
      background: white;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      width: 100%;
      max-width: 400px;
      padding: 32px;
    }

    .login-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
      justify-content: center;
    }

    .brand-icon {
      font-size: 2rem;
    }

    .login-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1a1a2e;
      margin: 0;
    }

    .login-subtitle {
      text-align: center;
      color: #64748b;
      font-size: 0.875rem;
      margin-bottom: 24px;
    }

    .login-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .form-group label {
      font-size: 0.875rem;
      font-weight: 500;
      color: #334155;
    }

    .form-input {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      font-size: 0.875rem;
      outline: none;
      transition: border-color 0.15s;
    }

    .form-input:focus {
      border-color: #3498db;
      box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
    }

    .error-message {
      font-size: 0.8rem;
      color: #dc2626;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      padding: 10px;
    }

    .login-btn {
      width: 100%;
      padding: 10px;
      background: #3498db;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s;
    }

    .login-btn:hover:not(:disabled) {
      background: #2980b9;
    }

    .login-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `]
})
export class LoginComponent {
  email = '';
  password = '';
  newPassword = '';
  confirmPassword = '';
  error = '';
  isLoading = false;
  showChangePassword = false;

  constructor(private authService: AuthService) {}

  async handleLogin(): Promise<void> {
    this.error = '';
    this.isLoading = true;

    try {
      const result = await this.authService.login(this.email, this.password);
      if (result.requiresPasswordChange) {
        this.showChangePassword = true;
      }
    } catch (err: any) {
      this.error = err.message || 'Échec de la connexion';
    } finally {
      this.isLoading = false;
    }
  }

  async handleChangePassword(): Promise<void> {
    this.error = '';

    if (this.newPassword !== this.confirmPassword) {
      this.error = 'Les mots de passe ne correspondent pas';
      return;
    }

    if (this.newPassword.length < 8) {
      this.error = 'Le mot de passe doit contenir au moins 8 caractères';
      return;
    }

    this.isLoading = true;

    try {
      await this.authService.confirmNewPassword(this.newPassword);
    } catch (err: any) {
      this.error = err.message || 'Échec du changement de mot de passe';
    } finally {
      this.isLoading = false;
    }
  }
}
